import { defineStore } from "pinia";
import _ from "lodash";
import * as _debug from "debug";
import ApiClient from "@/lib/ApiClient";
import { sureClone } from "@/lib/DataUtils";
import { createStore } from "../storeHelper";

const debug = _debug(`${process.env.VUE_APP_PREFIX}:OrganisationsStore`);

const DEFAULT_EDITOR = {
  id: null,
  name: null,
};

export const useOrganisationsStore = defineStore(
  "organisations",
  createStore({
    state: {
      organisations: {},
      editor: sureClone(DEFAULT_EDITOR),
    },

    getters: {
      organisationsList: (state) => _.values(state.organisations),
      organisationById: (state) => (id) => state.organisations[id],
      editorChanges() {
        return this.getEditorChanges("organisations");
      },
      editorHasChanges() {
        const diff = this.editorChanges;
        return diff && !_.isEmpty(diff);
      },
    },

    actions: {
      newOrganisationToEditor() {
        this.set("editor", sureClone(DEFAULT_EDITOR));
      },

      addOrganisation(organisation) {
        this.organisations[organisation.id] = organisation;
      },

      deleteOrganisation(organisationId) {
        delete this.organisations[organisationId];
      },

      async load() {
        debug("load");
        const organisations = await ApiClient.get(`/organisations`);
        debug("load", organisations);
        this.set("organisations", _.keyBy(organisations, "id"));
      },

      async loadOrganisationToEditor(organisationId) {
        debug("loadOrganisationToEditor", organisationId);
        const organisation = this.organisations[organisationId];
        if (!organisation) {
          throw new Error("Organisation not found");
        }
        this.set("editor", sureClone(organisation));
      },

      async createOrganisation() {
        const organisation = sureClone(this.editor);
        _.unset(organisation, "id");
        debug("createOrganisation", organisation);
        const response = await ApiClient.post("/organisations", organisation);
        debug("createOrganisation - response", response);
        this.addOrganisation(response);
        return response;
      },

      async saveOrganisationInEditor() {
        const organisationId = this.editor.id;
        if (!this.editorHasChanges) {
          debug("saveOrganisationInEditor", "No changes to save");
          return;
        }
        const modifiedOrganisation = sureClone(this.editor);
        debug("saveOrganisationInEditor", organisationId, modifiedOrganisation);
        const result = await ApiClient.put(
          `/organisations/${organisationId}`,
          modifiedOrganisation,
        );
        this.addOrganisation(result);
        debug("saveOrganisationInEditor, result", result);
        return result;
      },

      async deleteOrganisationById(organisationId) {
        debug("deleteOrganisation", organisationId);
        await ApiClient.delete(`/organisations/${organisationId}`);
        this.deleteOrganisation(organisationId);
      },

      async enableFeature(payloadOrOrganisationId, maybeFeatureId) {
        const organisationId =
          typeof payloadOrOrganisationId === "object"
            ? payloadOrOrganisationId?.organisationId
            : payloadOrOrganisationId;
        const featureId =
          typeof payloadOrOrganisationId === "object"
            ? payloadOrOrganisationId?.featureId
            : maybeFeatureId;

        debug("enableFeature", organisationId, featureId);

        if (!organisationId || !featureId) {
          throw new Error("OrganisationId and FeatureId are required");
        }

        const result = await ApiClient.post(
          `/organisations/${organisationId}/enable-feature`,
          {
            featureId,
          },
        );

        if (result?.id) {
          this.addOrganisation(result);
        }

        debug("enableFeature result", result);
        return result;
      },

      async disableFeature(payloadOrOrganisationId, maybeFeatureId) {
        const organisationId =
          typeof payloadOrOrganisationId === "object"
            ? payloadOrOrganisationId?.organisationId
            : payloadOrOrganisationId;
        const featureId =
          typeof payloadOrOrganisationId === "object"
            ? payloadOrOrganisationId?.featureId
            : maybeFeatureId;

        debug("disableFeature", organisationId, featureId);

        if (!organisationId || !featureId) {
          throw new Error("OrganisationId and FeatureId are required");
        }

        const result = await ApiClient.post(
          `/organisations/${organisationId}/disable-feature`,
          {
            featureId,
          },
        );

        if (result?.id) {
          this.addOrganisation(result);
        }

        return result;
      },
    },
  }),
);
